import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, post } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { clearSupportReturn } from "../../lib/support";
import { BrandMark } from "../../ui/Brand";
import { Button } from "../../ui/Button";
import { Icon, type IconName } from "../../ui/Icon";
import { usePlatformAuthed } from "./session";
import "./platform.css";

const NAV: { to: string; key: string; icon: IconName; end?: boolean }[] = [
  { to: "/yonetim", key: "overview", icon: "house", end: true },
  { to: "/yonetim/firmalar", key: "tenants", icon: "buildings" },
  { to: "/yonetim/kisiler", key: "people", icon: "magnifying-glass" },
  { to: "/yonetim/isler", key: "jobs", icon: "arrows-clockwise" },
  { to: "/yonetim/kayit", key: "audit", icon: "clock-counter-clockwise" },
  { to: "/yonetim/yoneticiler", key: "admins", icon: "user-gear" },
];

/**
 * 21ik Platform kabuğu: firma uygulamasının kabuğundan ayrı (firma menüsü yok). Üstte koyu platform çubuğu
 * ("başka bir yerdesiniz"), solda (telefonda üstte) platform menüsü. Aynı bileşenler ve tek açık tema.
 */
export function PlatformShell() {
  const { t } = useI18n();
  const { session, logout } = usePlatformAuthed();
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    mainRef.current?.focus({ preventScroll: true });
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="pshell">
      <a href="#main" className="skip-link">
        {t("platform.nav.skip")}
      </a>
      <header className="pshell__bar">
        <div className="pshell__brand">
          <BrandMark size={24} />
          <span className="pshell__name">{t("platform.name")}</span>
        </div>
        <div className="pshell__me">
          <span className="pshell__admin">{session.admin.displayName}</span>
          <button
            type="button"
            className="pshell__logout"
            onClick={async () => {
              await logout();
              navigate("/yonetim/giris", { replace: true });
            }}
          >
            <Icon name="sign-out" size={16} />
            <span>{t("platform.nav.logout")}</span>
          </button>
        </div>
      </header>
      <nav className="pshell__nav" aria-label={t("platform.nav.label")}>
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `pshell__link${isActive ? " is-active" : ""}`}>
            <Icon name={n.icon} size={20} />
            <span>{t(`platform.nav.${n.key}`)}</span>
          </NavLink>
        ))}
      </nav>
      <main id="main" ref={mainRef} tabIndex={-1} className="pshell__main">
        {session.activeSupport && <ActiveSupportNotice />}
        <Outlet />
      </main>
    </div>
  );
}

/** Panelde açık destek oturumu varsa: firma ekranına dönüş ya da bitirme. */
function ActiveSupportNotice() {
  const { t } = useI18n();
  const { session, refresh } = usePlatformAuthed();
  const qc = useQueryClient();
  const s = session.activeSupport!;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const left = Math.max(0, Math.ceil((Date.parse(s.expiresAt!) - Date.now()) / 60_000));
  return (
    <div className="notice notice--attention pshell__support" role="status">
      <Icon name="eye" size={20} />
      <div>
        <p className="pshell__support-title">{t("platform.activeSupport.title")}</p>
        <p>{t("platform.activeSupport.body", { company: s.tenantName, name: s.targetName, left: t("platform.common.minutesLeft", { count: left }) })}</p>
        {error && <p role="alert">{error}</p>}
        <div className="form__actions">
          <a className="btn btn--secondary btn--sm" href="/">
            <Icon name="arrow-square-out" size={16} />
            <span>{t("platform.activeSupport.goApp")}</span>
          </a>
          <Button
            size="sm"
            icon="x-circle"
            busy={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await post(`/platform/support-sessions/${s.id}/end`);
                clearSupportReturn();
              } catch (e) {
                if (!(e instanceof ApiError && e.status === 409)) setError(e instanceof ApiError ? e.message : t("errors.internal_error"));
              } finally {
                setBusy(false);
                await refresh();
                void qc.invalidateQueries({ queryKey: ["platform"] });
              }
            }}
          >
            {t("platform.activeSupport.end")}
          </Button>
        </div>
      </div>
    </div>
  );
}
